import React, { Fragment } from 'react';
import { IconCallBold, IconMailBold, Text } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Typography } from '@common/styles';

import { FooterButtonGroup } from './FooterButtonGroup';

interface Props {
  isInApp: boolean;
  isMobile: boolean;
  contactData: {
    customer_center_tel: string | null;
    manager_name: string | null | undefined;
    manager_tel: string | null | undefined;
    business_hours: string[];
    company_email: string | null | undefined;
  };
}
export const SellerContact: React.FC<React.PropsWithChildren<Props>> = ({
  isInApp,
  contactData: { customer_center_tel, business_hours, company_email },
}) => {
  const renderRow =
    (label = '') =>
    (...values: Array<React.ReactNode | null>) => {
      if (values.length === 1 && !values[0]) {
        return null;
      }
      return (
        <ContactRow className={Typography.CAPTION_12_MEDIUM}>
          <ContactLabel>{label}</ContactLabel>
          <ContactValuesWrapper>
            {values.map((value, i) => {
              if (typeof value === 'string') {
                return (
                  <ContactValues key={i} isMultiRows={values.length > 1}>
                    {value}
                  </ContactValues>
                );
              }
              return <Fragment key={i}>{value}</Fragment>;
            })}
          </ContactValuesWrapper>
        </ContactRow>
      );
    };

  const renderContactButtons = () => (
    <FooterButtonGroup>
      <LinkWrapper href={`tel:${customer_center_tel}`}>
        <IconCallBold size={16} color='iconPrimary' />
        <Text as='span' variant='Body_13_Medium' color='textPrimary'>
          전화하기
        </Text>
      </LinkWrapper>
      <LinkWrapper href={`mailto:${company_email}`}>
        <IconMailBold size={16} color='iconPrimary' />
        <Text as='span' variant='Body_13_Medium' color='textPrimary'>
          이메일 보내기
        </Text>
      </LinkWrapper>
    </FooterButtonGroup>
  );

  const renderOperatingHours = () => renderRow('운영시간')(...business_hours);

  return (
    <Section>
      <SectionTitle className={Typography.BODY_16_SEMIBOLD}>판매자 고객센터</SectionTitle>
      {isInApp ? (
        <>
          <RowContainer hasPaddingBottom>{renderOperatingHours()}</RowContainer>
          {renderContactButtons()}
        </>
      ) : (
        <RowContainer>
          {renderOperatingHours()}
          {renderContactButtons()}
        </RowContainer>
      )}
    </Section>
  );
};

const Section = styled.section`
  position: relative;
  padding: 0 16px 16px;

  &:last-child {
    margin: 0 0 80px;
  }
`;

const LinkWrapper = styled.a`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const SectionTitle = styled.h4`
  display: flex;
  align-items: center;
  height: 48px;
`;

const ContactRow = styled.div`
  display: flex;
  min-height: 32px;
  padding: 0 0 4px 0;
  font-weight: 500;
`;

const ContactLabel = styled.span`
  max-width: 120px;
  width: 100%;
  min-height: 14px;
  word-break: keep-all;
  color: ${vars.v3Color.gray500};
`;

const ContactValuesWrapper = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
`;

const ContactValues = styled.span<{ isMultiRows?: boolean }>`
  ${({ isMultiRows }) =>
    isMultiRows &&
    css`
      line-height: 140%;
    `}
  margin-right: 4px;
  word-break: keep-all;
  color: ${vars.v3Color.gray700};

  a {
    text-decoration: underline;
  }
`;

const RowContainer = styled.div<{ hasPaddingBottom?: boolean }>`
  padding: 8px 0 0 0;
  ${({ hasPaddingBottom }) => hasPaddingBottom && 'padding-bottom: 17px;'};
`;
